import React from 'react';
import PropTypes from 'prop-types';
import { Handles, Rail, Slider, Ticks, Tracks } from 'react-compound-slider';

import { findClosest } from '../../ducks/purchase/utils';
import { SliderHandle } from './SliderHandle';
import { SliderRail } from './SliderRail';
import { SliderTick } from './SliderTick';
import { SliderTrack } from './SliderTrack';
import Styles from './Slider.styles';

const createSourceOrTarget = (value, domain) => {
  const [minValue, maxValue] = domain;

  return {
    id: `$${value}`,
    value,
    percent: (100 * (value - minValue)) / (maxValue - minValue),
  };
};

const tracksWithReferenceValue = (getTrackProps, currentValue, domain, referenceValue) => {
  if (Number.isFinite(referenceValue)) {
    const isUpgrade = referenceValue < currentValue; // Otherwise, it's a downgrade
    const firstTrackTargetValue = isUpgrade ? referenceValue : currentValue;
    const secondTrackTargetValue = isUpgrade ? currentValue : referenceValue;
    const secondTrackClassName = `${Styles['no-left-radius']} ${Styles[isUpgrade ? 'upgrade' : 'downgrade']}`;

    return [
      <SliderTrack
        key="left-track"
        className={Styles['no-right-radius']}
        getTrackProps={getTrackProps}
        source={createSourceOrTarget(domain[0], domain)}
        target={createSourceOrTarget(firstTrackTargetValue, domain)}
      />,
      <SliderTrack
        key="right-track"
        className={secondTrackClassName}
        getTrackProps={getTrackProps}
        source={createSourceOrTarget(firstTrackTargetValue, domain)}
        target={createSourceOrTarget(secondTrackTargetValue, domain)}
      />,
    ];
  }

  // There was no subsription before
  return (
    <SliderTrack
      getTrackProps={getTrackProps}
      source={createSourceOrTarget(domain[0], domain)}
      target={createSourceOrTarget(currentValue, domain)}
    />
  );
};

export const ConcurrencySlider = ({
  allowedValues,
  className,
  currentValue,
  disabledTicks,
  formatTick,
  onChange,
  referenceValue,
  step,
  ticksValues,
}) => {
  const domain = [Math.min(...allowedValues), Math.max(...allowedValues)];
  const realTicksValues = ticksValues || allowedValues;

  return (
    <div className={className}>
      <Slider
        className={Styles.slider}
        domain={domain}
        mode={2}
        onChange={(values) => {
          // for some reason onChange is called twice. It might be related to the
          // currentValue property which is comming from the props and sets the value after
          // every update
          if (values[0] === currentValue) return;
          onChange(findClosest(values[0], allowedValues));
        }}
        step={step}
        values={[currentValue]}
      >
        <Rail>
          {({ getRailProps }) => <SliderRail getRailProps={getRailProps} />}
        </Rail>
        <Handles>
          {({ handles, getHandleProps }) => (
            <div>
              {handles.map((handle) => (
                <SliderHandle
                  key={handle.id}
                  domain={domain}
                  getHandleProps={getHandleProps}
                  handle={handle}
                />
              ))}
            </div>
          )}
        </Handles>
        {!disabledTicks && (
          <Ticks values={realTicksValues}>
            {({ ticks }) => (
              <div>
                {ticks.map((tick) => (
                  <SliderTick
                    key={tick.id}
                    active={currentValue >= tick.value}
                    count={realTicksValues.length}
                    format={formatTick}
                    tick={tick}
                  />
                ))}
              </div>
            )}
          </Ticks>
        )}
        <Tracks>
          {({ getTrackProps }) => (
            <div>
              {tracksWithReferenceValue(
                getTrackProps,
                currentValue,
                domain,
                referenceValue,
              )}
            </div>
          )}
        </Tracks>
      </Slider>
    </div>
  );
};

ConcurrencySlider.propTypes = {
  allowedValues: PropTypes.arrayOf(PropTypes.number).isRequired,
  className: PropTypes.string,
  currentValue: PropTypes.number.isRequired,
  disabledTicks: PropTypes.bool,
  formatTick: PropTypes.func,
  onChange: PropTypes.func,
  referenceValue: PropTypes.number,
  step: PropTypes.number,
  ticksValues: PropTypes.arrayOf(PropTypes.number),
};

ConcurrencySlider.defaultProps = {
  className: undefined,
  disabledTicks: false,
  formatTick: undefined,
  onChange: () => {},
  referenceValue: null,
  step: 1,
  ticksValues: null,
};

export default ConcurrencySlider;
